import React, { useState, useRef, useEffect } from "react";
import { Globe, Check } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

/**
 * Floating language selector.
 * - Fixed in the bottom-left corner across desktop, tablet and mobile.
 * - Subtle by default (compact pill showing the current locale).
 * - Expands inline to reveal EN · FR · ES; click outside or on an option to close.
 * - Uses the same palette as the rest of the site (cream + copper + ink).
 */
const pick = (obj, lang) => obj[lang] || obj.en || obj.fr || "";

const FloatingLanguageSwitcher = () => {
  const { lang, setLang, languages } = useLanguage();
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  // Close on outside click + Escape
  useEffect(() => {
    if (!open) return;
    const onPointer = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onPointer);
    document.addEventListener("touchstart", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointer);
      document.removeEventListener("touchstart", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div
      ref={wrapperRef}
      className="fixed bottom-5 left-5 md:bottom-7 md:left-7 z-40"
      data-testid="floating-language-switcher"
      data-cms-ui="true"
      data-no-cms-text="true"
    >
      {!open ? (
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label={pick({ en: "Change language", fr: "Changer de langue", es: "Cambiar idioma" }, lang)}
          aria-expanded="false"
          className="group inline-flex items-center gap-2 bg-[#FDFBF7]/90 backdrop-blur-md text-[#2C2621] border border-[#2C2621]/15 shadow-[0_4px_24px_rgba(28,22,19,0.08)] px-3.5 py-2.5 hover:border-[#C16542]/60 transition-colors"
          data-testid="floating-lang-trigger"
        >
          <Globe className="w-3.5 h-3.5 text-[#C16542]" strokeWidth={1.6} />
          <span className="text-[10px] tracking-[0.3em] uppercase font-medium">{lang}</span>
        </button>
      ) : (
        <div
          role="group"
          aria-label={pick({ en: "Language", fr: "Langue", es: "Idioma" }, lang)}
          className="inline-flex items-center gap-1 bg-[#FDFBF7]/95 backdrop-blur-md text-[#2C2621] border border-[#2C2621]/15 shadow-[0_4px_24px_rgba(28,22,19,0.10)] p-1"
          data-testid="floating-lang-options"
        >
          <span className="pl-2 pr-1 inline-flex items-center">
            <Globe className="w-3.5 h-3.5 text-[#C16542]" strokeWidth={1.6} />
          </span>
          {languages.map((l) => {
            const active = lang === l.code;
            return (
              <button
                key={l.code}
                type="button"
                onClick={() => {
                  setLang(l.code);
                  setOpen(false);
                }}
                aria-pressed={active}
                title={l.label}
                className={`inline-flex items-center gap-1.5 px-3 py-2 text-[10px] tracking-[0.3em] uppercase transition-colors ${
                  active
                    ? "bg-[#C16542] text-[#FDFBF7]"
                    : "text-[#2C2621] hover:text-[#C16542]"
                }`}
                data-testid={`floating-lang-option-${l.code}`}
              >
                {active && <Check className="w-3 h-3" strokeWidth={2.2} />}
                {l.code}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default FloatingLanguageSwitcher;
